import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const GestionScheduler = () => {
  const { API } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executionLogs, setExecutionLogs] = useState([]);

  useEffect(() => {
    cargarJobs();
    cargarLogs();
  }, []);

  const cargarJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/scheduler/jobs`, { headers });
      setJobs(response.data);
    } catch (error) {
      console.error('Error cargando jobs:', error);
      toast.error('Error al cargar configuración del scheduler');
    } finally {
      setLoading(false);
    }
  };

  const cargarLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/scheduler/logs?limit=50`, { headers });
      setExecutionLogs(response.data);
    } catch (error) {
      console.error('Error cargando logs:', error);
    }
  };

  const toggleJob = async (jobId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/scheduler/jobs/${jobId}/toggle`, { enabled }, { headers });
      toast.success(`Job ${enabled ? 'activado' : 'desactivado'} exitosamente`);
      cargarJobs();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cambiar estado del job');
    }
  };

  const ejecutarAhora = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/scheduler/jobs/${jobId}/run`, {}, { headers });
      toast.success('Job ejecutado manualmente');
      setTimeout(() => cargarLogs(), 2000);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al ejecutar job');
    }
  };

  const getJobIcon = (jobId) => {
    const icons = {
      'daily_notifications': 'fa-bell',
      'daily_class_reminders': 'fa-calendar-check',
      'evening_form_check': 'fa-clipboard-check',
      'delete_old_messages': 'fa-trash-alt',
      'daily_auto_backup': 'fa-database'
    };
    return icons[jobId] || 'fa-cog';
  };

  const getJobDescription = (jobId) => {
    const descriptions = {
      'daily_notifications': 'Revisa y envía notificaciones de noticias nuevas, eventos próximos, formularios por vencer y becas',
      'daily_class_reminders': 'Envía recordatorios diarios de clases a estudiantes',
      'evening_form_check': 'Revisa formularios que vencen pronto (segunda verificación del día)',
      'delete_old_messages': 'Elimina mensajes de chat antiguos (más de 30 días)',
      'daily_auto_backup': 'Crea backup automático de la base de datos'
    };
    return descriptions[jobId] || 'Tarea programada del sistema';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4"></i>
          <p className="text-gray-600">Cargando configuración del scheduler...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-t-4 border-t-purple-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fas fa-clock text-purple-600"></i>
            Gestión de Tareas Programadas (Scheduler)
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Administra las tareas automáticas del sistema que se ejecutan en horarios específicos
          </p>
        </CardHeader>
      </Card>

      {/* Jobs Programados */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="fas fa-calendar-alt text-blue-600"></i>
              Tareas Programadas Activas
            </div>
            <Button
              onClick={cargarJobs}
              variant="outline"
              size="sm"
            >
              <i className="fas fa-sync-alt mr-2"></i>
              Actualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      job.enabled ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <i className={`fas ${getJobIcon(job.id)} text-xl ${
                        job.enabled ? 'text-green-600' : 'text-gray-400'
                      }`}></i>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{job.name}</h3>
                        <Badge variant={job.enabled ? "success" : "secondary"}>
                          {job.enabled ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">
                        {getJobDescription(job.id)}
                      </p>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Horario:</span>
                          <p className="font-medium text-gray-900">
                            <i className="fas fa-clock mr-1"></i>
                            {job.schedule}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Última ejecución:</span>
                          <p className="font-medium text-gray-900">
                            {job.last_run ? new Date(job.last_run).toLocaleString('es-UY') : 'Nunca'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Próxima ejecución:</span>
                          <p className="font-medium text-gray-900">
                            {job.next_run ? new Date(job.next_run).toLocaleString('es-UY') : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => toggleJob(job.id, !job.enabled)}
                      variant={job.enabled ? "destructive" : "default"}
                      size="sm"
                    >
                      <i className={`fas fa-${job.enabled ? 'pause' : 'play'} mr-2`}></i>
                      {job.enabled ? 'Desactivar' : 'Activar'}
                    </Button>
                    
                    {job.enabled && (
                      <Button
                        onClick={() => ejecutarAhora(job.id)}
                        variant="outline"
                        size="sm"
                      >
                        <i className="fas fa-play-circle mr-2"></i>
                        Ejecutar Ahora
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Logs de Ejecución */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fas fa-history text-gray-600"></i>
            Historial de Ejecuciones (Últimas 50)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha/Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarea</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resultado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emails Enviados</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {executionLogs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(log.timestamp).toLocaleString('es-UY')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {log.job_name}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={log.status === 'success' ? 'success' : 'destructive'}>
                        {log.status === 'success' ? '✓ Exitoso' : '✗ Error'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-center">
                      {log.emails_sent || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.duration ? `${log.duration}s` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GestionScheduler;
